import {useState} from "react";
import {useQuery} from "@tanstack/react-query";
import {Ban, Check, CheckCheck, Clock3, Copy, Store} from "lucide-react";
import {apiGet} from "../../shared/api/client";
import {mapApiError} from "../../shared/api/errors";
import {useCapabilities} from "../../shared/auth/CapabilitiesContext";
import {DataState} from "../../shared/components/DataState";
import {useTranslation} from "../../shared/i18n";
import {formatNumber} from "../../shared/utils/format";

const inviteUrlFor = (agentId) => {
    if (!agentId || typeof window === "undefined") return "";
    return `${window.location.origin}/landing/registration?agent=${agentId}`;
};

const copyText = async (value) => {
    if (!value) return false;
    try {
        await navigator.clipboard.writeText(value);
        return true;
    } catch {
        return false;
    }
};

const loadAgentDashboard = () => apiGet("/api/v2/admin/agent/dashboard");

const Metric = ({label, value, note, icon: Icon, loading}) => (
    <article className="admin-card admin-metric-card">
        <div className="admin-metric-topline">
            <span>{label}</span>
            <Icon size={17} />
        </div>
        <p className="admin-card-value">{loading ? "—" : formatNumber(value)}</p>
        <p className="admin-card-note">{note}</p>
    </article>
);

export const AgentDashboardPage = () => {
    const {t} = useTranslation();
    const capabilities = useCapabilities();
    const inviteUrl = inviteUrlFor(capabilities.user?.id);
    const [copied, setCopied] = useState(false);
    const dashboardQuery = useQuery({
        queryKey: ["agent-dashboard"],
        queryFn: loadAgentDashboard,
        retry: false,
    });
    const summary = dashboardQuery.data || {};

    return (
        <div className="admin-page">
            <header className="admin-page-header">
                <div>
                    <h1 className="admin-page-title">{t("agent.title")}</h1>
                    <p className="admin-page-copy">{t("agent.copy")}</p>
                </div>
                {inviteUrl ? (
                    <div className="admin-page-actions">
                        <button
                            className="admin-button admin-button-primary"
                            type="button"
                            onClick={async () => {
                                const ok = await copyText(inviteUrl);
                                if (!ok) return;
                                setCopied(true);
                                window.setTimeout(() => setCopied(false), 2000);
                            }}
                        >
                            {copied ? <Check size={16} /> : <Copy size={16} />}
                            {copied ? t("agent.inviteCopied") : t("agent.invite")}
                        </button>
                        <p className="admin-card-note">{t("agent.inviteNote")}</p>
                        <p className="admin-muted">{inviteUrl}</p>
                    </div>
                ) : null}
            </header>
            {dashboardQuery.isError ? (
                <DataState
                    title={t("agent.errorTitle")}
                    copy={mapApiError(dashboardQuery.error).message || t("agent.errorCopy")}
                />
            ) : (
                <section className="admin-grid cols-4">
                    <Metric
                        label={t("agent.brought")}
                        value={summary.brought}
                        note={t("agent.broughtNote")}
                        icon={Store}
                        loading={dashboardQuery.isLoading}
                    />
                    <Metric
                        label={t("agent.succeeded")}
                        value={summary.succeeded}
                        note={t("agent.succeededNote")}
                        icon={CheckCheck}
                        loading={dashboardQuery.isLoading}
                    />
                    <Metric
                        label={t("agent.rejected")}
                        value={summary.rejected}
                        note={t("agent.rejectedNote")}
                        icon={Ban}
                        loading={dashboardQuery.isLoading}
                    />
                    <Metric
                        label={t("agent.pending")}
                        value={summary.pending}
                        note={t("agent.pendingNote")}
                        icon={Clock3}
                        loading={dashboardQuery.isLoading}
                    />
                </section>
            )}
        </div>
    );
};
